import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.business.gen';
import { AuthProvider } from './lib/auth/AuthProvider';
import { supabase } from './lib/supabaseClient.business';
import { broadcastDeskWiped } from './lib/desk/broadcast';
import { DeskEngine } from './lib/desk/engine';
import { DeskProvider } from './lib/desk/DeskProvider';
import { seedOnlineState } from './lib/desk/network';
import { createDeskInvoke } from './lib/desk/transport';
import { DeskBlockerModal } from './lib/desk/ui/DeskBlockerModal';
import { UpdateBar } from './lib/pwa/UpdateBar';
import './styles/global.css';

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

seedOnlineState();
const queryClient = new QueryClient();

// The check-in desk's offline queue (Task 14): TanStack paused mutations,
// persisted — allow-listed keys only — into an encrypted IndexedDB vault.
// Business only; the admin build persists nothing (entry-admin.tsx).
const desk: DeskEngine = new DeskEngine(queryClient, createDeskInvoke(supabase), {
  onWiped: broadcastDeskWiped,
  onKeyRevoked: () => desk.requestKeyRevokedWipe(),
});
// Replaces AuthProvider's plain queryClient.clear() on sign-out / account
// switch: guest data leaves memory at once, unsynced desk events do not.
const onSessionEnded = () => void desk.handleSignedOut();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('entry-business: #root element missing from index.html');

createRoot(rootEl).render(
  <StrictMode>
    <AuthProvider supabase={supabase} queryClient={queryClient} onSessionEnded={onSessionEnded}>
      <QueryClientProvider client={queryClient}>
        <DeskProvider engine={desk}>
          <RouterProvider router={router} />
          <DeskBlockerModal />
          <UpdateBar />
        </DeskProvider>
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
);
